/**
 * Development-only accessibility validation helpers
 *
 * These utilities help catch common accessibility issues during development
 * by logging warnings to the console. They only run in development mode and
 * have zero runtime impact in production.
 *
 * Usage: Import and call these validators in component render logic.
 *
 * @example
 * ```tsx
 * import { validateIconButton } from '@/shared/lib/accessibility-validator'
 *
 * export const MyButton = ({ 'aria-label': ariaLabel, size }) => {
 *   validateIconButton(!!ariaLabel, 'MyButton')
 *   // ... rest of component
 * }
 * ```
 */

import type { CSSProperties } from 'react'

/**
 * Detects if we're in development mode.
 *
 * Uses Vite's import.meta.env.DEV when available, falls back to checking
 * NODE_ENV. The cast to any is necessary because Vite's import.meta types
 * are environment-specific and may not be available in all build contexts.
 */
function isDev(): boolean {
  try {
    return Boolean(import.meta?.env?.DEV)
  } catch {
    return false
  }
}

/**
 * Validates that icon-only buttons have proper aria-label attributes
 *
 * Icon buttons without labels are invisible to screen reader users, creating
 * a poor accessibility experience. This validator helps catch these cases.
 *
 * @param hasLabel - Whether the button has an aria-label or visible text
 * @param componentName - Name of the component for better error messages
 */
export function validateIconButton(hasLabel: boolean, componentName: string) {
  if (isDev() && !hasLabel) {
    console.warn(
      `[A11y] ${componentName}: Icon-only button missing aria-label.\n` +
      `Screen reader users won't know what this button does.\n` +
      `Fix: Add aria-label="Descriptive action" to the button.`
    )
  }
}

/**
 * Validates that interactive divs have proper role and keyboard handlers
 *
 * Divs with click handlers need role="button" and keyboard support to be
 * accessible. Use semantic HTML (<button>) when possible instead.
 *
 * @param hasRole - Whether the div has an appropriate ARIA role
 * @param hasKeyboard - Whether the div has keyboard event handlers
 * @param componentName - Name of the component for better error messages
 */
export function validateInteractiveDiv(
  hasRole: boolean,
  hasKeyboard: boolean,
  componentName: string
) {
  if (isDev()) {
    if (!hasRole) {
      console.warn(
        `[A11y] ${componentName}: Interactive div missing role attribute.\n` +
        `Screen readers won't announce this as clickable.\n` +
        `Fix: Add role="button" or use semantic <button> element.`
      )
    }
    if (!hasKeyboard) {
      console.warn(
        `[A11y] ${componentName}: Interactive div missing keyboard handler.\n` +
        `Keyboard users can't activate this element.\n` +
        `Fix: Add onKeyDown handler for Enter/Space keys.`
      )
    }
  }
}

/**
 * Validates that form inputs have associated labels
 *
 * Inputs without labels are confusing for screen reader users and reduce
 * usability for all users.
 *
 * @param hasLabel - Whether the input has a label or aria-label
 * @param inputType - Type of input (for better error messages)
 * @param componentName - Name of the component for better error messages
 */
export function validateFormInput(
  hasLabel: boolean,
  inputType: string,
  componentName: string
) {
  if (isDev() && !hasLabel) {
    console.warn(
      `[A11y] ${componentName}: ${inputType} input missing label.\n` +
      `Users won't know what to enter in this field.\n` +
      `Fix: Add <label htmlFor="id"> or aria-label attribute.`
    )
  }
}

/**
 * Validates that images have alt text
 *
 * Images without alt text are invisible to screen readers and fail to convey
 * important information to visually impaired users.
 *
 * @param hasAlt - Whether the image has alt text
 * @param componentName - Name of the component for better error messages
 */
export function validateImageAlt(hasAlt: boolean, componentName: string) {
  if (isDev() && !hasAlt) {
    console.warn(
      `[A11y] ${componentName}: Image missing alt attribute.\n` +
      `Screen reader users won't know what this image shows.\n` +
      `Fix: Add alt="Description" (or alt="" for decorative images).`
    )
  }
}

/**
 * Validates that dynamic content has aria-live regions
 *
 * Content that changes dynamically should announce changes to screen readers
 * using aria-live regions.
 *
 * @param hasLiveRegion - Whether aria-live is set
 * @param componentName - Name of the component for better error messages
 */
export function validateDynamicContent(hasLiveRegion: boolean, componentName: string) {
  if (isDev() && !hasLiveRegion) {
    console.warn(
      `[A11y] ${componentName}: Dynamic content missing aria-live.\n` +
      `Screen readers won't announce changes to this content.\n` +
      `Fix: Add aria-live="polite" (or "assertive" for urgent changes).`
    )
  }
}

/**
 * Validates that loading states have proper announcements
 *
 * Loading states should be announced to screen readers so users know
 * content is being fetched.
 *
 * @param hasAriaLive - Whether loading state uses aria-live or aria-busy
 * @param componentName - Name of the component for better error messages
 */
export function validateLoadingState(hasAriaLive: boolean, componentName: string) {
  if (isDev() && !hasAriaLive) {
    console.warn(
      `[A11y] ${componentName}: Loading state not announced.\n` +
      `Screen readers won't know content is loading.\n` +
      `Fix: Add aria-busy="true" or aria-live="polite" with loading message.`
    )
  }
}

/**
 * Validates that tables have proper structure
 *
 * Tables need proper ARIA roles and labels for screen readers to navigate
 * them effectively.
 *
 * @param hasTableRole - Whether table has role="table"
 * @param hasLabel - Whether table has aria-label or aria-labelledby
 * @param componentName - Name of the component for better error messages
 */
export function validateTable(
  hasTableRole: boolean,
  hasLabel: boolean,
  componentName: string
) {
  if (isDev()) {
    if (!hasTableRole) {
      console.warn(
        `[A11y] ${componentName}: Table missing role="table".\n` +
        `Screen readers may not recognize this as a table.\n` +
        `Fix: Add role="table" to the table element.`
      )
    }
    if (!hasLabel) {
      console.warn(
        `[A11y] ${componentName}: Table missing label.\n` +
        `Users won't know what data this table contains.\n` +
        `Fix: Add aria-label="Description" to describe the table purpose.`
      )
    }
  }
}

/**
 * Validates that modals have proper focus management
 *
 * Modals should trap focus and return focus to the trigger element when closed.
 *
 * @param hasFocusTrap - Whether modal implements focus trapping
 * @param componentName - Name of the component for better error messages
 */
export function validateModal(hasFocusTrap: boolean, componentName: string) {
  if (isDev() && !hasFocusTrap) {
    console.warn(
      `[A11y] ${componentName}: Modal may be missing focus trap.\n` +
      `Keyboard users can tab out of the modal.\n` +
      `Fix: Use Radix Dialog or implement focus trap with inert attribute.`
    )
  }
}

/**
 * Helper to create screen-reader-only styles
 *
 * Returns CSS properties that hide content visually but keep it accessible
 * to screen readers.
 *
 * @returns CSSProperties object for sr-only elements
 */
export function srOnlyStyles(): CSSProperties {
  return {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  }
}

/**
 * Batch validator for common component accessibility issues
 *
 * Runs multiple validators at once for convenience.
 *
 * @param checks - Object with validation checks to run
 * @param componentName - Name of the component for better error messages
 */
export function validateAccessibility(
  checks: {
    iconButton?: { hasLabel: boolean }
    interactiveDiv?: { hasRole: boolean; hasKeyboard: boolean }
    formInput?: { hasLabel: boolean; inputType: string }
    image?: { hasAlt: boolean }
    table?: { hasTableRole: boolean; hasLabel: boolean }
    modal?: { hasFocusTrap: boolean }
  },
  componentName: string
) {
  if (!isDev()) return

  if (checks.iconButton) {
    validateIconButton(checks.iconButton.hasLabel, componentName)
  }
  if (checks.interactiveDiv) {
    validateInteractiveDiv(
      checks.interactiveDiv.hasRole,
      checks.interactiveDiv.hasKeyboard,
      componentName
    )
  }
  if (checks.formInput) {
    validateFormInput(
      checks.formInput.hasLabel,
      checks.formInput.inputType,
      componentName
    )
  }
  if (checks.image) {
    validateImageAlt(checks.image.hasAlt, componentName)
  }
  if (checks.table) {
    validateTable(
      checks.table.hasTableRole,
      checks.table.hasLabel,
      componentName
    )
  }
  if (checks.modal) {
    validateModal(checks.modal.hasFocusTrap, componentName)
  }
}
